import axios from "axios";
import config from "../configuration/config";
import { msalInstance, msalReady } from "../auth/msalInstance";
import { apiTokenRequest } from "../auth/msalConfig";
import { getRequestBranchIds, getStoredBranchIds } from "../auth/branchRequestContext";

function normalizeRoles(rawRoles) {
  if (!rawRoles) return [];
  if (Array.isArray(rawRoles)) return rawRoles;
  return [rawRoles];
}

function normalizeBranchIds(raw) {
  if (!raw) return [];

  const toId = (v) => {
    if (v == null) return null;
    if (typeof v === "object") {
      const candidate =
        v.id ?? v.Id ?? v.branchId ?? v.BranchId ?? v.terminalId ?? v.TerminalId;
      return candidate == null ? null : String(candidate).trim();
    }
    return String(v).trim();
  };

  const arr = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(",")
      : [raw];

  const normalized = arr
    .map(toId)
    .filter((v) => typeof v === "string" && v.length > 0);

  return [...new Set(normalized)];
}

function getBranchIdsFromClaims(claims) {
  if (!claims || typeof claims !== "object") return [];

  const sources = [
    claims.branchIds,
    claims.BranchIds,
    claims.branchId,
    claims.BranchId,
    claims.terminalIds,
    claims.TerminalIds,
    claims.terminals,
    claims.branches,
    claims.Branches,
  ];

  const all = sources.flatMap((s) => normalizeBranchIds(s));
  return [...new Set(all)];
}

const baseURL =
  typeof config.backendUrl === "string" && config.backendUrl.length > 0
    ? (config.backendUrl.endsWith("/") ? config.backendUrl : `${config.backendUrl}/`)
    : undefined;

export const api = axios.create({
  baseURL,
  timeout: 100000,
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// GET request deduplication – prevents identical concurrent GET requests.
// If a GET with the same URL+params is already in-flight, the second caller
// receives the same promise instead of firing a new HTTP request.
// ---------------------------------------------------------------------------
const pendingGets = new Map();

function buildDedupeKey(config) {
  const url = config.url || "";
  const params = config.params ? JSON.stringify(config.params, Object.keys(config.params).sort()) : "";
  return `${url}::${params}`;
}

const originalGet = api.get.bind(api);
api.get = function deduplicatedGet(url, config = {}) {
  const fullConfig = { ...config, url };
  const key = buildDedupeKey(fullConfig);

  if (pendingGets.has(key)) {
    return pendingGets.get(key);
  }

  const promise = originalGet(url, config).finally(() => {
    pendingGets.delete(key);
  });

  pendingGets.set(key, promise);
  return promise;
};

api.interceptors.request.use(async (request) => {
  await msalReady;

  const account =
    msalInstance.getActiveAccount() || msalInstance.getAllAccounts()?.[0];


  if (!account) return request;

  try {
    const tokenResp = await msalInstance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
    });

    request.headers = request.headers || {};
    request.headers.Authorization = `Bearer ${tokenResp.accessToken}`;

    const claims = account?.idTokenClaims || {};
    const roles = normalizeRoles(claims.roles || claims.role || claims.groups);
    const isSuperUser = roles.includes("ROLE_SUPERUSER");
    const runtimeBranchIds = getRequestBranchIds();
    const claimBranchIds = getBranchIdsFromClaims(claims);
    const storedBranchIds = getStoredBranchIds();
    const branchIds =
      runtimeBranchIds.length > 0
        ? runtimeBranchIds
        : claimBranchIds.length > 0
          ? claimBranchIds
          : storedBranchIds;

    if (!isSuperUser && branchIds.length > 0) {
      request.headers["X-Branch-Id"] = branchIds.join(",");
    } else if (isSuperUser && request.headers["X-Branch-Id"]) {
      delete request.headers["X-Branch-Id"];
    }
    
    return request;
  } catch (e) {
    
    return request;
  }
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 403 &&
      window.location.pathname.startsWith("/administration")
    ) {
      window.location.href = "/no-access";
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

export default api;
